// packages/ui/src/auth/RoleGuard.tsx
//
// Per-portal route gate (Amendment #4).
//
// The cookie scope is parent-domain `.v3.printway.work` so one login
// shares session across all 3 V3 SPAs. Without an FE role-guard, a user
// who logged in at admin-v3 can navigate to merchant-v3 and see the
// merchant shell render → every action then 403s on the BE. This
// component is the FE side of the contract: render <WrongPortalPage>
// instead of the shell when the user's scope doesn't match the portal.
//
// BE security holds independently (RBAC middleware enforces 403). This
// is purely a UX gate to prevent the broken "shell renders, actions
// fail" loop documented in B-04.

import type { ReactNode } from 'react';
import type { AuthUser } from '../hooks/useAuth.js';
import { WrongPortalPage } from './WrongPortalPage.js';

export type Portal = 'admin' | 'merchant' | 'vendor';

/**
 * Role types that grant admin-portal access. Mirrors backend
 * `ADMIN_ROLE_TYPES` in adminMerchantsRead.handler.ts:120 to keep the
 * UX gate in lockstep with the BE authorization gate.
 */
// Phase 8.2 added Accountant + Support to the admin portal
// (finance/catalog/support surfaces). Mirrors AuthGuard.isAdminPortalUser
// (apps/admin/src/layouts/AuthGuard.tsx) which gates the SPA shell.
// BE RBAC middleware enforces per-route permissions independently; this
// set only governs the UX-level portal match (WrongPortalPage vs shell).
const ADMIN_ROLE_TYPES = new Set([
  'admin',
  'superadmin',
  'support',
  'accountant',
]);

/**
 * Returns true when the user's auth payload matches the given portal.
 *
 *   - admin    → primaryRole/roles[] ∈ {admin, superadmin}
 *   - merchant → merchantId truthy
 *   - vendor   → vendorId truthy
 *
 * The check is intentionally permissive (uses primaryRole OR roles[])
 * because admin users may be granted (`roles[]`) without their
 * `primaryRole` changing — see B-04-bug-2 grant-role flow.
 */
export function userMatchesPortal(user: AuthUser, portal: Portal): boolean {
  switch (portal) {
    case 'admin':
      return (
        ADMIN_ROLE_TYPES.has(user.primaryRole) ||
        (user.roles?.some((r) => ADMIN_ROLE_TYPES.has(r)) ?? false)
      );
    case 'merchant':
      return Boolean(user.merchantId);
    case 'vendor':
      return Boolean(user.vendorId);
    default: {
      const _exhaustive: never = portal;
      void _exhaustive;
      return false;
    }
  }
}

interface RoleGuardProps {
  user: AuthUser;
  portal: Portal;
  children: ReactNode;
}

/**
 * Wrap an authenticated layout subtree. If the user is signed in but
 * doesn't have a membership/role for this portal, render
 * <WrongPortalPage> with a deep link to the correct portal.
 *
 * Caller must already have ensured user is non-null (e.g., the
 * existing `if (!user) return <Navigate to="/login" />` block stays
 * one level above this component).
 */
export function RoleGuard({
  user,
  portal,
  children,
}: RoleGuardProps): JSX.Element {
  if (!userMatchesPortal(user, portal)) {
    return <WrongPortalPage user={user} currentPortal={portal} />;
  }
  return <>{children}</>;
}
